import { useEffect, useState, useCallback } from 'react';
import { guiasApi, materialApi, type Guia, type Material } from '../lib/api';
import { Toast, type ToastState } from '../components/Toast';

const TONE: Record<string, { soft: string; ink: string; solid: string; line: string }> = {
  copper: { soft: 'var(--copper-soft)', ink: 'var(--copper-ink)', solid: 'var(--copper)', line: 'var(--copper-line)' },
  sage:   { soft: 'var(--sage-soft)',   ink: 'var(--sage-ink)',   solid: 'var(--sage)',   line: 'var(--sage-line)'   },
  amber:  { soft: 'var(--amber-soft)',  ink: 'var(--amber)',      solid: 'var(--amber)',  line: '#e7d7ab'            },
  clay:   { soft: 'var(--clay-soft)',   ink: 'var(--clay)',       solid: 'var(--clay)',   line: '#e7c9c1'            },
  info:   { soft: 'var(--info-soft)',   ink: 'var(--info)',       solid: 'var(--info)',   line: '#cbd9e2'            },
  sand:   { soft: 'var(--sand-soft)',   ink: '#7a6047',           solid: 'var(--sand)',   line: '#e3d3bd'            },
};

const CATS = [
  { id: 'ANSIEDAD',             label: 'Ansiedad',    tone: 'copper' },
  { id: 'DEPRESIÓN',            label: 'Depresión',   tone: 'info'   },
  { id: 'AUTOESTIMA',           label: 'Autoestima',  tone: 'sage'   },
  { id: 'RELACIONES',           label: 'Relaciones',  tone: 'clay'   },
  { id: 'ESTRÉS',               label: 'Estrés',      tone: 'amber'  },
  { id: 'DUELO',                label: 'Duelo',       tone: 'sand'   },
  { id: 'TRAUMA',               label: 'Trauma',      tone: 'info'   },
  { id: 'MINDFULNESS',          label: 'Mindfulness', tone: 'sage'   },
  { id: 'LÍMITES',              label: 'Límites',     tone: 'copper' },
  { id: 'CRECIMIENTO PERSONAL', label: 'Crecimiento', tone: 'amber'  },
];
const catById = Object.fromEntries(CATS.map(c => [c.id, c]));

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][d.getMonth()]} ${d.getFullYear()}`;
}
function fmt(n: number) { return n.toLocaleString('es-MX'); }

function CatTag({ cat, small }: { cat: string; small?: boolean }) {
  const c = catById[cat];
  if (!c) return <span className="tag">{cat}</span>;
  const t = TONE[c.tone];
  return (
    <span className="tag" style={{ background: t.soft, color: t.ink, borderColor: t.line, height: small ? 21 : 23 }}>
      <span className="dot" style={{ background: t.solid }} />
      {c.label}
    </span>
  );
}

/* ── Upload modal ── */
function UploadGuideModal({ onClose, onUpload }: {
  onClose: () => void;
  onUpload: (d: { cat: string; title: string; file: File }) => void;
}) {
  const [cat, setCat]     = useState('');
  const [title, setTitle] = useState('');
  const [file, setFile]   = useState<File | null>(null);
  const [uploading, setUp] = useState(false);

  const ready = cat && title.trim() && file;

  const submit = () => {
    if (!file || !cat || !title.trim()) return;
    setUp(true);
    onUpload({ cat, title: title.trim(), file });
  };

  return (
    <div className="modal" onClick={() => !uploading && onClose()}
      style={{ background: 'rgba(47,37,25,.38)', backdropFilter: 'blur(2px)' }}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div style={{ padding: '22px 24px 0' }}>
          <div className="row" style={{ gap: 11 }}>
            <span className="stat-ico" style={{ background: 'var(--info-soft)', color: 'var(--info)', width: 38, height: 38 }}>
              <UploadIco size={19} />
            </span>
            <div>
              <h3 style={{ margin: 0, fontSize: 18 }}>Subir guía PDF</h3>
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>Se vinculará automáticamente a la categoría</p>
            </div>
            <button className="btn btn-ghost btn-icon" style={{ marginLeft: 'auto' }} onClick={onClose} disabled={uploading}>
              <XIco size={17} />
            </button>
          </div>
        </div>
        <hr className="hr" style={{ margin: '18px 0 0' }} />

        {!uploading ? (
          <div style={{ padding: 24 }}>
            <label className="field-label">Título de la guía</label>
            <input className="input" style={{ marginBottom: 18 }}
              placeholder="Ej. Dormir con la mente en calma"
              value={title} onChange={e => setTitle(e.target.value)} />

            <label className="field-label">Categoría</label>
            <div className="row" style={{ flexWrap: 'wrap', gap: 7, marginBottom: 18 }}>
              {CATS.map(c => (
                <button key={c.id} onClick={() => setCat(c.id === cat ? '' : c.id)}
                  style={{ cursor: 'pointer', height: 30, padding: '0 13px', fontSize: 12.5, fontWeight: 600,
                    border: cat === c.id ? `1px solid ${TONE[c.tone].solid}` : '1px solid var(--border)',
                    borderRadius: 99, background: cat === c.id ? TONE[c.tone].soft : 'var(--surface-2)',
                    color: cat === c.id ? TONE[c.tone].ink : 'var(--ink-2)' }}>
                  {c.label}
                </button>
              ))}
            </div>

            <label className="field-label">Archivo PDF</label>
            <label style={{ width: '100%', height: 110, cursor: 'pointer', flexDirection: 'column', gap: 8,
              border: `1.5px dashed ${file ? 'var(--sage)' : 'var(--border-2)'}`,
              borderRadius: 'var(--r)', background: file ? 'var(--sage-soft)' : 'var(--surface-2)',
              color: file ? 'var(--sage-ink)' : 'var(--muted)', fontFamily: 'var(--sans)', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <input type="file" accept=".pdf,application/pdf" style={{ display: 'none' }}
                onChange={e => setFile(e.target.files?.[0] ?? null)} />
              {file
                ? <><FileIco size={22} /><b>{file.name}</b><span style={{ fontSize: 12 }}>{(file.size/1024/1024).toFixed(1)} MB · clic para cambiar</span></>
                : <><UploadIco size={22} /><span>Arrastra el PDF o haz clic para seleccionar</span></>
              }
            </label>

            {cat && (
              <div className="row" style={{ gap: 9, marginTop: 14, padding: '10px 13px',
                background: 'var(--info-soft)', borderRadius: 'var(--r-sm)', border: '1px solid #cbd9e2' }}>
                <span style={{ color: 'var(--info)' }}><LinkIco size={15} /></span>
                <span style={{ fontSize: 12.5, color: '#3c5567' }}>
                  Se enviará cuando alguien solicite la guía de <b>{catById[cat]?.label ?? cat}</b>.
                </span>
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div style={{ color: 'var(--info)', marginBottom: 14 }}><RefreshIco size={30} /></div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 18 }}>Subiendo y vinculando…</div>
          </div>
        )}

        {!uploading && (
          <div className="drawer-foot" style={{ justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={submit} disabled={!ready}>
              <UploadIco size={16} /> Subir guía
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main page ── */
export default function Guias() {
  const [guias, setGuias]         = useState<Guia[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [uploadOpen, setUpload]   = useState(false);
  const [toast, setToast]         = useState<ToastState | null>(null);
  const [activeMap, setActiveMap] = useState<Record<string, boolean>>({});

  const showToast = useCallback((msg: string, type: ToastState['type']) => setToast({ message: msg, type }), []);

  const loadGuias = useCallback(() => {
    setLoading(true);
    guiasApi.getGuias()
      .then(r => {
        setGuias(r.guias);
        const m: Record<string, boolean> = {};
        r.guias.forEach(g => { m[g.id] = !!g.activa; });
        setActiveMap(m);
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadGuias(); }, [loadGuias]);

  const handleUpload = async (d: { cat: string; title: string; file: File }) => {
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => { const r = reader.result as string; resolve(r.split(',')[1]); };
        reader.onerror = reject;
        reader.readAsDataURL(d.file);
      });
      await guiasApi.uploadGuia({ categoria: d.cat, titulo: d.title, pdf_base64: base64, nombre_archivo: d.file.name });
      showToast(`Guía vinculada a ${catById[d.cat]?.label ?? d.cat}`, 'success');
      setUpload(false);
      loadGuias();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al subir', 'error');
      setUpload(false);
    }
  };

  const toggleGuide = (id: string) => {
    setActiveMap(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const linkedCats = new Set(guias.filter(g => activeMap[g.id] ?? !!g.activa).map(g => g.categoria));
  const activeCount = Object.values(activeMap).filter(Boolean).length;

  return (
    <div className="page page-wide">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 28, margin: 0 }}>Guías</h1>
          <p className="muted" style={{ margin: '7px 0 0', fontSize: 14, maxWidth: 560 }}>
            Catálogo de guías psicoeducativas en PDF. Cada guía se vincula a una categoría y se envía automáticamente por DM cuando alguien la solicita.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setUpload(true)}>
          <UploadIco size={16} /> Subir guía
        </button>
      </div>

      {/* Stats */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 28 }}>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--info-soft)', color: 'var(--info)' }}><BookIco /></span>
          </div>
          <div className="stat-num tabular">{guias.length}</div>
          <div className="stat-label">Guías en catálogo</div>
          <div className="stat-delta flat">{activeCount} activas</div>
        </div>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--sage-soft)', color: 'var(--sage)' }}><LinkIco /></span>
          </div>
          <div className="stat-num tabular">
            {linkedCats.size}<span style={{ fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 400, color: 'var(--muted)' }}>/{CATS.length}</span>
          </div>
          <div className="stat-label">Categorías cubiertas</div>
          <div className="stat-delta flat">{CATS.length - linkedCats.size} sin guía</div>
        </div>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--copper-soft)', color: 'var(--copper)' }}><DownloadIco /></span>
          </div>
          <div className="stat-num tabular">{fmt(guias.length * 100)}</div>
          <div className="stat-label">Entregas por DM</div>
          <div className="stat-delta up"><ArrowUpIco size={13} /> acumulado</div>
        </div>
      </div>

      {/* Missing categories */}
      {CATS.length - linkedCats.size > 0 && (
        <div className="card card-pad row" style={{ gap: 12, marginBottom: 24, borderColor: '#e7d7ab', background: 'var(--amber-soft)' }}>
          <span style={{ color: 'var(--amber)' }}><InfoIco /></span>
          <span style={{ fontSize: 13.5, color: '#7a5e22' }}>Categorías aún sin guía:</span>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {CATS.filter(c => !linkedCats.has(c.id)).map(c => (
              <span key={c.id} className="tag" style={{ background: '#fff', borderColor: '#e7d7ab' }}>{c.label}</span>
            ))}
          </div>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div className="loading-center"><span className="spinner" /> Cargando guías…</div>}

      {!loading && guias.length === 0 && (
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <span style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--surface-2)', display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: 'var(--ink-2)' }}>
            <BookIco size={24} />
          </span>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 17, color: 'var(--ink)' }}>Sin guías</div>
          <div style={{ fontSize: 13, marginTop: 4, color: 'var(--muted)' }}>Sube la primera guía para empezar.</div>
        </div>
      )}

      {!loading && guias.length > 0 && (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {guias.map(g => {
            const c = catById[g.categoria];
            const t = c ? (TONE[c.tone] ?? TONE.copper) : TONE.copper;
            const isActive = activeMap[g.id] ?? !!g.activa;
            return (
              <div key={g.id} className="card fade-up" style={{ opacity: isActive ? 1 : 0.72, transition: 'opacity .2s' }}>
                <div className="card-pad" style={{ display: 'flex', gap: 14, paddingBottom: 14 }}>
                  <div style={{ width: 56, height: 72, borderRadius: 8, flex: '0 0 56px',
                    background: t.soft, border: `1px solid ${t.line}`, display: 'grid', placeItems: 'center', color: t.solid }}>
                    <PdfIco size={24} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontFamily: 'var(--serif)', fontSize: 16.5, lineHeight: 1.25, marginBottom: 5 }}>{g.titulo}</div>
                    <div className="muted" style={{ fontSize: 11.5, marginBottom: 7 }}>{g.nombre_archivo}</div>
                    <CatTag cat={g.categoria} small />
                  </div>
                </div>
                <hr className="hr" />
                <div className="card-pad" style={{ paddingTop: 12, paddingBottom: 12 }}>
                  <div className="row" style={{ gap: 8, marginBottom: 8, fontSize: 12.5, color: 'var(--ink-2)' }}>
                    <LinkIco size={13} />
                    <span>Vinculada a <span className="kbd" style={{ fontSize: 11 }}>{g.categoria}</span></span>
                  </div>
                  <div className="row" style={{ justifyContent: 'space-between', fontSize: 12.5, color: 'var(--muted)' }}>
                    <span className="row" style={{ gap: 6 }}><CalendarIco size={13} /> {fmtDate(g.created_at)}</span>
                    <span className="row" style={{ gap: 6 }}><DownloadIco size={13} /> —</span>
                  </div>
                </div>
                <hr className="hr" />
                <div className="row card-pad" style={{ paddingTop: 11, paddingBottom: 11, gap: 8 }}>
                  {/* Toggle switch */}
                  <button onClick={() => toggleGuide(g.id)} className="row"
                    style={{ gap: 7, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5,
                      fontWeight: 600, color: isActive ? 'var(--sage-ink)' : 'var(--muted)', padding: 0 }}>
                    <span style={{ width: 30, height: 17, borderRadius: 99, position: 'relative', transition: 'background .15s', flexShrink: 0,
                      background: isActive ? 'var(--sage)' : 'var(--border-2)', display: 'inline-block' }}>
                      <span style={{ position: 'absolute', top: 2, left: isActive ? 15 : 2, width: 13, height: 13,
                        borderRadius: 99, background: '#fff', transition: 'left .15s', display: 'block' }} />
                    </span>
                    {isActive ? 'Activa' : 'Pausada'}
                  </button>
                  <a href={g.url_r2} target="_blank" rel="noopener noreferrer"
                    className="btn btn-sm btn-ghost" style={{ marginLeft: 'auto', textDecoration: 'none' }}>
                    <EyeIco size={14} /> Ver
                  </a>
                  <a href={g.url_r2} download className="btn btn-sm btn-ghost" style={{ textDecoration: 'none' }}>
                    <DownloadIco size={14} /> PDF
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Material de apoyo ── */}
      <MaterialSection showToast={showToast} />

      {uploadOpen && <UploadGuideModal onClose={() => setUpload(false)} onUpload={handleUpload} />}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

/* ── Material section component ── */
const TIPOS = ['Plantilla', 'Ejercicio', 'Material didáctico'];

function MaterialSection({ showToast }: { showToast: (msg: string, type: ToastState['type']) => void }) {
  const [material, setMaterial]     = useState<Material[]>([]);
  const [loading, setLoading]       = useState(true);
  const [filterCat, setFilterCat]   = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [uploadOpen, setUpload]     = useState(false);

  const loadMaterial = useCallback(() => {
    setLoading(true);
    materialApi.getMaterial()
      .then(r => setMaterial(r.material))
      .catch(() => setMaterial([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadMaterial(); }, [loadMaterial]);

  const handleDelete = async (id: string) => {
    try {
      await materialApi.deleteMaterial(id);
      showToast('Material eliminado', 'success');
      loadMaterial();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al eliminar', 'error');
    }
  };

  const filtered = material.filter(m =>
    (!filterCat  || m.categoria === filterCat) &&
    (!filterTipo || m.tipo.toLowerCase() === filterTipo.toLowerCase())
  );

  return (
    <div style={{ marginTop: 44 }}>
      <hr className="hr" style={{ marginBottom: 32 }} />
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 22, margin: 0 }}>Material de apoyo</h2>
          <p className="muted" style={{ margin: '6px 0 0', fontSize: 13.5 }}>
            Plantillas, ejercicios y material didáctico complementario a las guías.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setUpload(true)}>
          <UploadIco size={16} /> Subir material
        </button>
      </div>

      {/* Filters */}
      <div className="row" style={{ gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <select className="input" style={{ width: 'auto', minWidth: 160, height: 36, fontSize: 13 }}
          value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="">Todas las categorías</option>
          {CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select className="input" style={{ width: 'auto', minWidth: 160, height: 36, fontSize: 13 }}
          value={filterTipo} onChange={e => setFilterTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {(filterCat || filterTipo) && (
          <button className="btn btn-ghost btn--sm" onClick={() => { setFilterCat(''); setFilterTipo(''); }}>
            <XIco size={14} /> Limpiar
          </button>
        )}
      </div>

      {loading && <div className="loading-center"><span className="spinner" /> Cargando material…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ padding: '40px 20px', textAlign: 'center', background: 'var(--surface-2)', borderRadius: 'var(--r)', border: '1px dashed var(--border-2)' }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🗂</div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 16, color: 'var(--ink)' }}>Sin material</div>
          <div style={{ fontSize: 13, marginTop: 4, color: 'var(--muted)' }}>
            {filterCat || filterTipo ? 'No hay material con estos filtros.' : 'Sube el primer material de apoyo.'}
          </div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(m => {
            const c = catById[m.categoria];
            const t = c ? (TONE[c.tone] ?? TONE.copper) : TONE.copper;
            return (
              <div key={m.id} className="card card-pad row" style={{ gap: 14, alignItems: 'center' }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: t.soft, border: `1px solid ${t.line}`, display: 'grid', placeItems: 'center', color: t.solid }}>
                  <FileIco size={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14.5, marginBottom: 3 }}>{m.titulo}</div>
                  <div className="row" style={{ gap: 7 }}>
                    <CatTag cat={m.categoria} small />
                    <span className="tag" style={{ background: 'var(--surface-2)', color: 'var(--ink-2)', fontSize: 11.5, height: 21 }}>
                      {m.tipo}
                    </span>
                  </div>
                  {m.descripcion && (
                    <div className="muted" style={{ fontSize: 12, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}>
                      {m.descripcion}
                    </div>
                  )}
                </div>
                <div className="row" style={{ gap: 6, flexShrink: 0 }}>
                  <a href={m.url_r2} target="_blank" rel="noopener noreferrer"
                    className="btn btn-sm btn-ghost" style={{ textDecoration: 'none' }}>
                    <EyeIco size={14} /> Ver
                  </a>
                  <button className="btn btn-sm btn-ghost" style={{ color: 'var(--clay)' }}
                    onClick={() => { if (window.confirm('¿Eliminar este material?')) handleDelete(m.id); }}>
                    <XIco size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {uploadOpen && <UploadMaterialModal onClose={() => setUpload(false)} onUpload={async (d) => {
        try {
          await materialApi.uploadMaterial(d);
          showToast('Material subido correctamente', 'success');
          setUpload(false);
          loadMaterial();
        } catch (e) {
          showToast(e instanceof Error ? e.message : 'Error al subir', 'error');
          setUpload(false);
        }
      }} />}
    </div>
  );
}

/* ── Upload material modal ── */
function UploadMaterialModal({ onClose, onUpload }: {
  onClose: () => void;
  onUpload: (d: { categoria: string; tipo: string; titulo: string; descripcion?: string; archivo_base64: string; nombre_archivo: string }) => void;
}) {
  const [cat, setCat]           = useState('');
  const [tipo, setTipo]         = useState('');
  const [title, setTitle]       = useState('');
  const [desc, setDesc]         = useState('');
  const [file, setFile]         = useState<File | null>(null);
  const [uploading, setUp]      = useState(false);

  const ready = cat && tipo && title.trim() && file;

  const submit = async () => {
    if (!file || !cat || !tipo || !title.trim()) return;
    setUp(true);
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => { const r = reader.result as string; resolve(r.split(',')[1]); };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    onUpload({ categoria: cat, tipo, titulo: title.trim(), descripcion: desc.trim() || undefined, archivo_base64: base64, nombre_archivo: file.name });
  };

  return (
    <div className="modal" onClick={() => !uploading && onClose()}
      style={{ background: 'rgba(47,37,25,.38)', backdropFilter: 'blur(2px)' }}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div style={{ padding: '22px 24px 0' }}>
          <div className="row" style={{ gap: 11 }}>
            <span className="stat-ico" style={{ background: 'var(--sage-soft)', color: 'var(--sage)', width: 38, height: 38 }}>
              <FileIco size={19} />
            </span>
            <div>
              <h3 style={{ margin: 0, fontSize: 18 }}>Subir material de apoyo</h3>
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>Plantilla, ejercicio o material didáctico</p>
            </div>
            <button className="btn btn-ghost btn-icon" style={{ marginLeft: 'auto' }} onClick={onClose} disabled={uploading}>
              <XIco size={17} />
            </button>
          </div>
        </div>
        <hr className="hr" style={{ margin: '18px 0 0' }} />

        {!uploading ? (
          <div style={{ padding: 24 }}>
            <label className="field-label">Título</label>
            <input className="input" style={{ marginBottom: 14 }}
              placeholder="Ej. Diario de pensamientos automáticos"
              value={title} onChange={e => setTitle(e.target.value)} />

            <label className="field-label">Descripción (opcional)</label>
            <input className="input" style={{ marginBottom: 14 }}
              placeholder="Breve descripción del material"
              value={desc} onChange={e => setDesc(e.target.value)} />

            <label className="field-label">Categoría</label>
            <div className="row" style={{ flexWrap: 'wrap', gap: 7, marginBottom: 14 }}>
              {CATS.map(c => (
                <button key={c.id} onClick={() => setCat(c.id === cat ? '' : c.id)}
                  style={{ cursor: 'pointer', height: 28, padding: '0 11px', fontSize: 12, fontWeight: 600,
                    border: cat === c.id ? `1px solid ${TONE[c.tone].solid}` : '1px solid var(--border)',
                    borderRadius: 99, background: cat === c.id ? TONE[c.tone].soft : 'var(--surface-2)',
                    color: cat === c.id ? TONE[c.tone].ink : 'var(--ink-2)' }}>
                  {c.label}
                </button>
              ))}
            </div>

            <label className="field-label">Tipo</label>
            <div className="row" style={{ gap: 7, marginBottom: 14 }}>
              {TIPOS.map(tp => (
                <button key={tp} onClick={() => setTipo(tp === tipo ? '' : tp)}
                  style={{ cursor: 'pointer', height: 30, padding: '0 13px', fontSize: 12.5, fontWeight: 600,
                    border: tipo === tp ? '1px solid var(--sage)' : '1px solid var(--border)',
                    borderRadius: 99, background: tipo === tp ? 'var(--sage-soft)' : 'var(--surface-2)',
                    color: tipo === tp ? 'var(--sage-ink)' : 'var(--ink-2)' }}>
                  {tp}
                </button>
              ))}
            </div>

            <label className="field-label">Archivo</label>
            <label style={{ width: '100%', height: 100, cursor: 'pointer', flexDirection: 'column', gap: 8,
              border: `1.5px dashed ${file ? 'var(--sage)' : 'var(--border-2)'}`,
              borderRadius: 'var(--r)', background: file ? 'var(--sage-soft)' : 'var(--surface-2)',
              color: file ? 'var(--sage-ink)' : 'var(--muted)', fontFamily: 'var(--sans)', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <input type="file" style={{ display: 'none' }}
                onChange={e => setFile(e.target.files?.[0] ?? null)} />
              {file
                ? <><FileIco size={20} /><b>{file.name}</b><span style={{ fontSize: 11.5 }}>{(file.size/1024/1024).toFixed(1)} MB · clic para cambiar</span></>
                : <><UploadIco size={20} /><span>Arrastra el archivo o haz clic para seleccionar</span></>
              }
            </label>
          </div>
        ) : (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div style={{ color: 'var(--sage)', marginBottom: 14 }}><RefreshIco size={30} /></div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 18 }}>Subiendo material…</div>
          </div>
        )}

        {!uploading && (
          <div className="drawer-foot" style={{ justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={submit} disabled={!ready}>
              <UploadIco size={16} /> Subir material
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Icons ── */
function Svg({ children, size = 18 }: { children: React.ReactNode; size?: number }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
}
function BookIco({ size = 18 }: { size?: number })     { return <Svg size={size}><path d="M5 4h9a3 3 0 0 1 3 3v13H8a3 3 0 0 0-3 3z" transform="translate(1 0)"/><path d="M4 4a2 2 0 0 0-2 2v13a3 3 0 0 1 3-3h1"/></Svg>; }
function UploadIco({ size = 18 }: { size?: number })   { return <Svg size={size}><path d="M12 16V4m-5 5 5-5 5 5"/><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></Svg>; }
function DownloadIco({ size = 18 }: { size?: number }) { return <Svg size={size}><path d="M12 4v12m-5-5 5 5 5-5"/><path d="M4 20h16"/></Svg>; }
function LinkIco({ size = 18 }: { size?: number })     { return <Svg size={size}><path d="M9 15l6-6"/><path d="M11 7l1-1a3.5 3.5 0 0 1 5 5l-1 1M13 17l-1 1a3.5 3.5 0 0 1-5-5l1-1"/></Svg>; }
function PdfIco({ size = 18 }: { size?: number })      { return <Svg size={size}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="M8.5 13.5h1a1 1 0 0 1 0 2h-1zM8.5 13.5v4M13 13.5v4M13 13.5h1.5M13 15.5h1.2M16.5 13.5h1.5M16.5 13.5v4"/></Svg>; }
function FileIco({ size = 18 }: { size?: number })     { return <Svg size={size}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5M8.5 13h7M8.5 17h5"/></Svg>; }
function CalendarIco({ size = 18 }: { size?: number }) { return <Svg size={size}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/></Svg>; }
function EyeIco({ size = 18 }: { size?: number })      { return <Svg size={size}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></Svg>; }
function InfoIco({ size = 18 }: { size?: number })     { return <Svg size={size}><circle cx="12" cy="12" r="8.5"/><path d="M12 11v5M12 8h.01"/></Svg>; }
function XIco({ size = 18 }: { size?: number })        { return <Svg size={size}><path d="M6 6l12 12M18 6 6 18"/></Svg>; }
function RefreshIco({ size = 18 }: { size?: number })  { return <Svg size={size}><path d="M3 12a9 9 0 0 1 15.5-6.3M21 4v4h-4"/><path d="M21 12a9 9 0 0 1-15.5 6.3M3 20v-4h4"/></Svg>; }
function ArrowUpIco({ size = 18 }: { size?: number })  { return <Svg size={size}><path d="M12 19V5M6 11l6-6 6 6"/></Svg>; }
